/**
 * Sizes our Seatics chrome box to the height Seatics actually laid out against.
 *
 * Seatics builds its mobile UI from window.innerHeight — it records that value as
 * Seatics.config.startingViewportHeight — and positions #list-ctn as
 * position:fixed with height = startingViewportHeight minus its own top offset.
 *
 * Our box ([data-seatics-chrome]) uses overflow:hidden plus translateZ(0) so those
 * fixed descendants stay contained instead of escaping to the viewport. When the
 * box is shorter than the height Seatics assumed, the difference is cut off the
 * BOTTOM of the ticket list. Measured on staging at 390px wide:
 *
 *   mobile Safari    innerHeight 664, box min(74vh,720px) = 478  ->  187px cut
 *   attendee WebView innerHeight 610, box min(74vh,720px) = 439  ->  171px cut
 *
 * That cut is why the last ticket rows could never be fully reached on any
 * browser, and why the native app (shorter viewport, so a proportionally smaller
 * 74vh box) lost even more rows. Sizing the box to the assumed height removes the
 * cut entirely: fully visible rows went 2/6 -> 4/6 in Safari and 1/6 -> 4/6 in the
 * WebView, with the quantity drawer still unclipped.
 *
 * Any viewport number we pick ourselves is a guess about what Seatics did, and
 * in the attendee app the guess is wrong: startingViewportHeight is recorded
 * once at init, while Seatics re-lays out #list-ctn against the live viewport
 * afterwards. When the two disagree the mismatch is directly visible —
 * measured in the app at 393px wide:
 *
 *   box taller than Seatics' layout  -> dead space under the last ticket row
 *                                       before the hero image, and the grey
 *                                       scrim runs past the quantity drawer
 *   box shorter than Seatics' layout -> the bottom ticket rows are cut off
 *
 * So we do not guess. #list-ctn is position:fixed and our box carries
 * translateZ(0), which makes the box its containing block: the list's offset
 * inside the box is whatever Seatics computed, independent of how tall the box
 * currently is. Sizing the box to that list's bottom edge makes our clipping
 * box exactly Seatics' own layout height, and it re-tracks automatically every
 * time Seatics recomputes (drawer open/close, map expand, WebView resize).
 *
 * The viewport-derived height stays as the fallback for the window before
 * Seatics has rendered a list.
 *
 * One case needs more than Seatics' layout height. The quantity sheet is sized
 * to the band, so on a short viewport its Additional Notes are taller than the
 * sheet and Seatics leaves the remainder to the sheet's own overflow. WKWebView
 * does not clip that overflow — neither on the position:fixed sheet nor on the
 * position:relative scroller inside it — so it paints the last notes onto the
 * scrim below the sheet, which reads as the drawer greying out its own content.
 * Rather than fight the clip, remove the overflow: while a sheet is open in the
 * app, grow the band until the sheet fits its content, then stretch the sheet
 * into that band. Both halves are needed — measured in the app, growing the
 * band alone left it at 636 with the sheet still 513, because Seatics sizes the
 * sheet in pixels when it opens and never re-follows the band. Both revert the
 * moment the sheet closes.
 *
 * Mobile only. Desktop keeps Seatics' bounded two-column layout.
 */

import { NATIVE_WEBVIEW_HTML_ATTR } from './seaticsPrecheckoutWebViewFix';

export const SEATICS_CHROME_SELECTOR = '[data-seatics-chrome="1"]';
export const SEATICS_VIEWPORT_HEIGHT_ATTR = 'data-promotix-seatics-viewport-h';
/** Seatics' own fixed ticket-column container, the element it sizes itself. */
export const SEATICS_LIST_SELECTOR = '#list-ctn';
/** The open quantity sheet, and the scroller Seatics puts its content in. */
export const SEATICS_SHEET_SELECTOR = '#precheckout-parent, .sea-precheckout-parent-active';
export const SEATICS_SHEET_CONTENT_SELECTOR =
  '#sea-pre-checkout-wrapper, .sea-pre-checkout-wrapper';
/** The sheet is the band less its borders; spare pixels keep content off the edge. */
export const SEATICS_SHEET_SLACK = 4;
export const SEATICS_SHEET_HEIGHT_ATTR = 'data-promotix-seatics-sheet-h';
/** Seatics.config.smallBreakpoint — its own mobile/desktop split. */
export const SEATICS_SMALL_BREAKPOINT = 991;
/** Guards against sizing to a half-built or absurd Seatics layout. */
export const SEATICS_LAID_OUT_MIN = 320;
export const SEATICS_LAID_OUT_MAX = 1400;

const SIZED_PROPS = ['height', 'max-height', 'min-height'];

/**
 * Height Seatics assumed when it laid out its fixed mobile UI, never reported as
 * shorter than the viewport we can actually see right now.
 */
export const seaticsAssumedViewportHeight = (win = typeof window !== 'undefined' ? window : null) => {
  const starting = Number.parseFloat(win?.Seatics?.config?.startingViewportHeight);
  const inner = Number(win?.innerHeight);
  const live = Number.isFinite(inner) && inner > 0 ? Math.round(inner) : 0;
  if (Number.isFinite(starting) && starting > 0) return Math.max(Math.round(starting), live);
  return live;
};

/**
 * Height Seatics actually laid out inside this box, read off its own fixed
 * ticket column rather than inferred from the viewport.
 *
 * @returns {number} px height, or 0 when Seatics has not laid out a list yet.
 */
export const seaticsLaidOutHeight = box => {
  if (!box?.getBoundingClientRect || !box.querySelector) return 0;
  const list = box.querySelector(SEATICS_LIST_SELECTOR);
  if (!list?.getBoundingClientRect) return 0;

  const listRect = list.getBoundingClientRect();
  if (!(listRect.height > 0)) return 0;

  const height = Math.round(listRect.bottom - box.getBoundingClientRect().top);
  if (height < SEATICS_LAID_OUT_MIN || height > SEATICS_LAID_OUT_MAX) return 0;
  return height;
};

/**
 * Band height needed for an open quantity sheet to fit its own content, so the
 * sheet has no overflow for WKWebView to paint onto the scrim.
 *
 * Prefer the sheet's own scrollHeight: Seatics sometimes parks "Worry-Free
 * Guarantee" (and similar) outside #sea-pre-checkout-wrapper, which made the
 * white card stop short of the real content.
 *
 * @returns {number} px height, or 0 when no sheet is open or it already fits.
 */
export const seaticsOpenSheetHeight = box => {
  if (!box?.querySelector || !box.getBoundingClientRect) return 0;
  const sheet = box.querySelector(SEATICS_SHEET_SELECTOR);
  if (!sheet?.getBoundingClientRect) return 0;

  const sheetRect = sheet.getBoundingClientRect();
  if (!(sheetRect.height > 0)) return 0;

  const wrapper = sheet.querySelector?.(SEATICS_SHEET_CONTENT_SELECTOR);
  const wrapperHeight = Number(wrapper?.scrollHeight) || 0;
  const sheetScrollHeight = Number(sheet.scrollHeight) || 0;
  const contentHeight = Math.max(wrapperHeight, sheetScrollHeight);
  if (!Number.isFinite(contentHeight) || contentHeight <= 0) return 0;

  const offsetInBox = sheetRect.top - box.getBoundingClientRect().top;
  const height = Math.ceil(offsetInBox + contentHeight) + SEATICS_SHEET_SLACK;
  if (height < SEATICS_LAID_OUT_MIN || height > SEATICS_LAID_OUT_MAX) return 0;
  return height;
};

/**
 * Band height so an open quantity sheet can fill the visible WebView — from the
 * band's top down to the bottom of the available viewing space. Used only in
 * the attendee native app; Safari keeps Seatics' own sheet height.
 *
 * @returns {number} px height, or 0 when no sheet is open / viewport unknown.
 */
export const seaticsViewportFillBandHeight = (
  box,
  win = typeof window !== 'undefined' ? window : null
) => {
  if (!box?.querySelector || !box.getBoundingClientRect || !win) return 0;
  const sheet = box.querySelector(SEATICS_SHEET_SELECTOR);
  const sheetRect = sheet?.getBoundingClientRect?.();
  if (!(sheetRect?.height > 0)) return 0;

  const viewportH =
    Number(win.visualViewport?.height) || Number(win.innerHeight) || 0;
  if (!(viewportH > 0)) return 0;

  const boxTop = box.getBoundingClientRect().top;
  // Reach the bottom of the visible webview (small slack for subpixel rounding).
  const height = Math.ceil(viewportH - boxTop - 1);
  if (height < SEATICS_LAID_OUT_MIN || height > SEATICS_LAID_OUT_MAX) return 0;
  return height;
};

/**
 * Stretches the open sheet down to the bottom of the band it lives in.
 *
 * Growing the band alone is not enough: measured in the app, the band was 636
 * with the sheet still at 513, so 121px of notes rendered past the white card
 * onto the scrim. Seatics sizes the sheet in pixels when it opens and does not
 * re-follow the band afterwards, so the sheet has to be told directly.
 *
 * @returns {number} px height applied, or 0 when no sheet needed stretching.
 */
export const fitSeaticsSheetToBand = box => {
  if (!box?.querySelector || !box.getBoundingClientRect) return 0;
  const sheet = box.querySelector(SEATICS_SHEET_SELECTOR);
  const sheetRect = sheet?.getBoundingClientRect?.();
  if (!(sheet instanceof HTMLElement) || !(sheetRect?.height > 0)) {
    // Seatics reuses the sheet node, so a stale height must not survive a close.
    clearSeaticsSheetHeight(box);
    return 0;
  }

  const boxRect = box.getBoundingClientRect();
  const offsetInBox = sheetRect.top - boxRect.top;
  const height = Math.floor(boxRect.height - offsetInBox - 1);
  if (height <= 0 || height <= Math.round(sheetRect.height)) return 0;

  sheet.style.setProperty('height', `${height}px`, 'important');
  sheet.style.setProperty('max-height', 'none', 'important');
  sheet.setAttribute(SEATICS_SHEET_HEIGHT_ATTR, String(height));
  return height;
};

export const clearSeaticsSheetHeight = (
  root = typeof document !== 'undefined' ? document : null
) => {
  const doc =
    root?.nodeType === 9
      ? root
      : root?.ownerDocument || (typeof document !== 'undefined' ? document : null);
  if (!doc?.querySelectorAll) return;
  doc.querySelectorAll(`[${SEATICS_SHEET_HEIGHT_ATTR}]`).forEach(el => {
    el.removeAttribute(SEATICS_SHEET_HEIGHT_ATTR);
    if (!(el instanceof HTMLElement)) return;
    el.style.removeProperty('height');
    el.style.removeProperty('max-height');
  });
};

export const clearSeaticsViewportHeight = (
  root = typeof document !== 'undefined' ? document : null
) => {
  const doc =
    root?.nodeType === 9
      ? root
      : root?.ownerDocument || (typeof document !== 'undefined' ? document : null);
  if (!doc?.querySelectorAll) return;
  doc.querySelectorAll(`[${SEATICS_VIEWPORT_HEIGHT_ATTR}]`).forEach(el => {
    el.removeAttribute(SEATICS_VIEWPORT_HEIGHT_ATTR);
    if (!(el instanceof HTMLElement)) return;
    SIZED_PROPS.forEach(prop => el.style.removeProperty(prop));
  });
};

/**
 * @returns {number} px height applied, or 0 when nothing was sized.
 */
export const syncSeaticsViewportHeight = (
  root = typeof document !== 'undefined' ? document : null,
  win = typeof window !== 'undefined' ? window : null
) => {
  const doc =
    root?.nodeType === 9
      ? root
      : root?.ownerDocument || (typeof document !== 'undefined' ? document : null);
  if (!doc?.querySelectorAll || !win) return 0;

  const width = Number(win.innerWidth);
  if (Number.isFinite(width) && width > SEATICS_SMALL_BREAKPOINT) {
    clearSeaticsViewportHeight(doc);
    return 0;
  }

  const fallback = seaticsAssumedViewportHeight(win);
  // Only the app needs the sheet to fit without overflow; every other browser
  // clips and scrolls it the way Seatics intended.
  const growForSheet =
    doc.documentElement?.getAttribute?.(NATIVE_WEBVIEW_HTML_ATTR) === '1';

  let applied = 0;
  doc.querySelectorAll(SEATICS_CHROME_SELECTOR).forEach(box => {
    if (!(box instanceof HTMLElement)) return;
    const laidOut = seaticsLaidOutHeight(box) || fallback;
    const height = growForSheet
      ? Math.max(
          laidOut,
          seaticsOpenSheetHeight(box),
          // Fill the available WebView so the white card reaches the bottom
          // chrome instead of stopping mid-notes (Worry-Free Guarantee bleed).
          seaticsViewportFillBandHeight(box, win)
        )
      : laidOut;
    if (!height) return;
    box.style.setProperty('height', `${height}px`, 'important');
    // The base rule caps at 800px and floors at 400px; both would reintroduce a
    // mismatch with what Seatics laid out.
    box.style.setProperty('max-height', 'none', 'important');
    box.style.setProperty('min-height', '0', 'important');
    box.setAttribute(SEATICS_VIEWPORT_HEIGHT_ATTR, String(height));
    applied = height;
    // The band is only room; the sheet has to be stretched into it.
    if (growForSheet) fitSeaticsSheetToBand(box);
  });
  if (!growForSheet) clearSeaticsSheetHeight(doc);
  if (!applied) clearSeaticsViewportHeight(doc);
  return applied;
};

export const installSeaticsViewportSizing = (cancelled = () => false) => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return () => {};

  let debounce = null;
  const run = () => {
    if (cancelled()) return;
    syncSeaticsViewportHeight(document, window);
  };
  const scheduleRun = () => {
    if (cancelled()) return;
    if (debounce) clearTimeout(debounce);
    debounce = setTimeout(run, 16);
  };

  run();
  window.addEventListener('resize', run);
  window.addEventListener('orientationchange', run);
  if (window.visualViewport) window.visualViewport.addEventListener('resize', run);

  // Seatics opens the quantity sheet without a window resize. Re-fit when the
  // drawer mounts / class flips so native WKWebView stretches the white card
  // to the available viewing space (Safari ignores growForSheet).
  let observer = null;
  if (typeof MutationObserver !== 'undefined') {
    observer = new MutationObserver(scheduleRun);
    observer.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'id'],
    });
  }

  return () => {
    if (debounce) clearTimeout(debounce);
    if (observer) observer.disconnect();
    window.removeEventListener('resize', run);
    window.removeEventListener('orientationchange', run);
    if (window.visualViewport) window.visualViewport.removeEventListener('resize', run);
    clearSeaticsViewportHeight(document);
    clearSeaticsSheetHeight(document);
  };
};
